import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Autocomplete,
	Grid,
	Divider,
	TextField,
	LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Badge, Typography,  List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, Chip, Stack, Drawer, Tabs, Tab, Popper, Fade, Paper, Alert, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link } from '@mui/material';

import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import MouseOutlinedIcon from "@mui/icons-material/MouseOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PageTitle from "../../components/PageTitle/PageTitle";
import Datagrid from "../../components/Datagrid/Datagrid";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import ReactECharts from "echarts-for-react";
import DateRange from "../../components/DateRange/DateRange";
import ChartLoading from "../../components/Carts/ChartLoading/ChartLoading";
import { getUserAnalyticsDashboard } from "../../services/openpanelAnalyticsService";
import { getAllUsers } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getProvisionedMachines as getMachinesByProvision } from "../../services/manageSiteMachinesService";
import { getUserAllocations } from "../../services/manageSitesService";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import { useOptions } from "../../context/optionContext";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { canAccessUserAnalytics } from "../../utilities/pageAccess";

const DATE_RANGE_BUTTONS = [
	{ key: "T", label: "Today" },
	{ key: "W", label: "Week" },
	{ key: "M", label: "Month" },
	{ key: "Q", label: "Quarter" },
	{ key: "Y", label: "Year" },
];

const FILTER_KEYS = [
	{ key: "customer_id", label: "Customer", allLabel: "All Customers" },
	{ key: "site_id", label: "Sites", allLabel: "All Sites" },
	{ key: "user_email", label: "User", allLabel: "All Users" },
	{ key: "machine_id", label: "Machine", allLabel: "All Machines" },
];

const ALL_VALUE = "All";
const ALL_OPTION = { value: ALL_VALUE, label: ALL_VALUE, searchText: "all" };
const OPTION_LIMIT = 2000;

const isAllValue = (value) =>
	value === undefined || value === null || value === "" || value === ALL_VALUE;

const withAllOption = (options = []) => [
	ALL_OPTION,
	...options.filter((option) => option?.value !== ALL_VALUE),
];

const normalizeSearchValue = (value) =>
	String(value || "")
		.toLowerCase()
		.replace(/[-_/.,:()]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const buildSearchText = (...values) => normalizeSearchValue(values.filter(Boolean).join(" "));

const getSearchQuery = (inputValue, selectedOption) => {
	const query = normalizeSearchValue(inputValue);
	if (!query) return "";
	const selectedLabel = normalizeSearchValue(selectedOption?.label);
	return query === selectedLabel ? "" : query;
};

const normalizeCustomerNumber = (value) => String(value || "").trim().toLowerCase();

const normalizeSiteNumber = (value) => String(value || "").trim().toLowerCase();

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const normalizeMachineNumber = (value) => String(value || "").trim().toUpperCase();

const toPositiveNumber = (value) => {
	const number = Number(value);
	return Number.isFinite(number) && number > 0 ? number : null;
};

const getCustomerId = (customer) =>
	toPositiveNumber(customer?.cus_id ?? customer?.cust_id ?? customer?.customer_id ?? customer?.id);

const getSiteId = (site) =>
	toPositiveNumber(site?.site_id ?? site?.customer_site_id ?? site?.id);

const getSiteCustomerId = (site) =>
	toPositiveNumber(site?.cus_id ?? site?.cust_id ?? site?.customer_id ?? site?.customer?.cus_id);

const getMachineId = (machine) =>
	toPositiveNumber(machine?.machine_id ?? machine?.id);

const getMachineSiteId = (machine) =>
	toPositiveNumber(machine?.site_id ?? machine?.customer_site_id);

const getMachineCustomerId = (machine) =>
	toPositiveNumber(machine?.cus_id ?? machine?.customer_id ?? machine?.cust_id);

const getSiteSelectionValue = (site) => {
	const siteId = getSiteId(site);
	const customerId = getSiteCustomerId(site);
	if (!siteId) return "";
	return customerId ? `${siteId}:${customerId}` : String(siteId);
};

const getFilterSiteId = (value) => toPositiveNumber(String(value || "").split(":")[0]);

const getFilterSiteCustomerId = (value) => {
	const [, customerId] = String(value || "").split(":");
	return toPositiveNumber(customerId);
};

const uniqueBy = (items = [], getKey) => {
	const seen = new Set();
	return items.filter((item) => {
		const key = getKey(item);
		if (!key || seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const getCustomerOptions = (customers = []) =>
	customers.map((customer) => ({
		value: getCustomerId(customer),
		label: [customer.customer_name, customer.customer_number].filter(Boolean).join(" - "),
		searchText: buildSearchText(
			customer.customer_name,
			customer.customer_number,
			customer.parent_customer_name,
			customer.parent_customer_number,
			getCustomerId(customer)
		),
	}));

const getSiteOptions = (sites = []) =>
	sites.map((site) => {
		const siteName =
			site.customer_site_name ||
			site.site_name ||
			site.address_name_ln ||
			site.customer_site_number ||
			`Site ${getSiteId(site) || ""}`.trim();
		const customerName = site.parent_customer_name || site.customer_name || "";
		const customerNumber = site.parent_customer_number || site.customer_number || "";
		return {
			value: getSiteSelectionValue(site),
			label: [siteName, site.customer_site_number].filter(Boolean).join(" - "),
			searchText: buildSearchText(
				siteName,
				site.customer_site_name,
				site.customer_site_number,
				site.site_name,
				customerName,
				customerNumber,
				getSiteId(site),
				getSiteCustomerId(site)
			),
			customerName,
			customerNumber,
			customer_id: getSiteCustomerId(site),
			site_id: getSiteId(site),
		};
	});

const uniqueMachinesByNumber = (machines = []) => {
	const seen = new Map();
	machines.forEach((machine) => {
		const key =
			normalizeMachineNumber(machine?.machine_number) ||
			normalizeMachineNumber(machine?.machine_name) ||
			String(machine?.machine_id || "");
		if (!key || seen.has(key)) return;
		seen.set(key, machine);
	});
	return Array.from(seen.values());
};

const getMachineOptions = (machines = []) =>
	uniqueMachinesByNumber(machines).map((machine) => ({
		key: `${machine.machine_id}_${machine.site_id}_${machine.cus_id}`,
		value: getMachineId(machine),
		label: `${machine.machine_name} - ${machine.machine_number}`,
		machine_number: machine.machine_number,
		searchText: buildSearchText(
			machine.machine_name,
			machine.machine_number,
			machine.customer_site_name,
			machine.customer_site_number,
			machine.customer_name,
			machine.customer_number,
			getMachineId(machine),
			getMachineSiteId(machine),
			getMachineCustomerId(machine)
		),
	}));

const getUserOptions = (users = []) =>
	uniqueBy(
		users
			.filter((user) => normalizeEmail(user?.user_email))
			.map((user) => ({
				value: user.user_email,
				label: `${user.user_display_name} (${user.user_email})`,
				displayName: user.user_display_name,
				email: user.user_email,
				searchText: buildSearchText(user.user_display_name, user.user_email, user.user_role_name, user.user_role),
				user_email: user.user_email,
			})),
		(option) => normalizeEmail(option.user_email)
	);

const isSuperAdminRow = (row) => Number(row?.user_id) === 1;

const isAdminRow = (row) => {
	if (isSuperAdminRow(row)) return true;
	const roleToken =
		row?.user_role_name ||
		row?.user_role ||
		row?.role?.role_name ||
		row?.role?.role_type ||
		row?.user_type ||
		"";
	const normalized = String(roleToken).trim().toLowerCase();
	return normalized === "admin" || normalized.includes("admin");
};

const isApprovedUser = (row) => {
	const statusValue = row?.user_status ?? row?.status ?? null;
	if (Number(statusValue) === 1) return true;
	const normalized = String(statusValue || "").trim().toLowerCase();
	return normalized === "approved" || normalized === "active";
};

const getUserAllocatedSiteIds = (userRow) => {
	if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
		return Array.from(
			new Set(
				userRow.allocations
					.map((allocation) => Number(allocation?.site_id))
					.filter((siteId) => Number.isFinite(siteId) && siteId > 0)
			)
		);
	}
	if (Array.isArray(userRow?.user_sites)) {
		return Array.from(
			new Set(
				userRow.user_sites
					.map((siteId) => Number(siteId))
					.filter((siteId) => Number.isFinite(siteId) && siteId > 0)
			)
		);
	}
	const rawSites = `${userRow?.user_sites || ""}`.trim();
	if (!rawSites) return [];
	return rawSites
		.split(",")
		.map((siteId) => Number(siteId))
		.filter((siteId) => Number.isFinite(siteId) && siteId > 0);
};

const getUserAllocatedCustomerIds = (userRow) => {
	if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
		return Array.from(
			new Set(
				userRow.allocations
					.map((allocation) => Number(allocation?.cus_id))
					.filter((customerId) => Number.isFinite(customerId) && customerId > 0)
			)
		);
	}
	if (Array.isArray(userRow?.user_customers)) {
		return Array.from(
			new Set(
				userRow.user_customers
					.map((customerId) => Number(customerId))
					.filter((customerId) => Number.isFinite(customerId) && customerId > 0)
			)
		);
	}
	const rawCustomers = `${userRow?.user_customers || ""}`.trim();
	if (!rawCustomers) return [];
	return rawCustomers
		.split(",")
		.map((customerId) => Number(customerId))
		.filter((customerId) => Number.isFinite(customerId) && customerId > 0);
};

const getAccessibleSiteIdsForUser = (userRow, scopedSites) => {
	const allocatedIds = getUserAllocatedSiteIds(userRow);
	if (allocatedIds.length > 0) return allocatedIds;
	const customerIds = getUserAllocatedCustomerIds(userRow);
	if (customerIds.length > 0) {
		const allowedCustomerIds = new Set(customerIds);
		return scopedSites
			.filter((site) => allowedCustomerIds.has(getSiteCustomerId(site)))
			.map((site) => getSiteId(site))
			.filter(Boolean);
	}
	if (isAdminRow(userRow)) return scopedSites.map((site) => getSiteId(site)).filter(Boolean);
	return [];
};

const formatNumber = (value) =>
	new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(value || 0));

const formatDuration = (seconds) => {
	const totalSeconds = Math.max(0, Number(seconds || 0));
	const minutes = Math.floor(totalSeconds / 60);
	const remainingSeconds = Math.round(totalSeconds % 60);
	return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
};

const getPageLabel = (path) => {
	if (!path) return "Unknown Page";

	const [routePath = "/", queryString = ""] = String(path).split("#")[0].split("?");
	const params = new URLSearchParams(queryString);
	const normalizedPath = routePath || "/";

	if (normalizedPath.startsWith("/admin")) {
		return "Admin Page";
	}

	if (normalizedPath === "/machines" && params.get("site_line_id")) {
		const lineName = params.get("line_name");
		if (lineName) return lineName;

		const lineNumber = params.get("line_number");
		if (lineNumber) return `Line ${lineNumber}`;

		return "Machine Line Page";
	}

	// Dynamic route
	if (/^\/machines\/[^/]+$/.test(normalizedPath)) {
		return "Machine Details";
	}

	// Static routes
	const routeMap = {
		"/login": "Login Page",
		"/register": "Register Page",
		"/forgot-password": "Forgot Password Page",
		"/reset-password": "Reset Password Page",
		"/reset-password/:token": "Reset Password With Token Page",
		"*": "Page Not Found",

		"/verify-email": "Verify Email Page",
		"/accept-eula": "Accept EULA Page",

		"/": "Home Page",
		"/home": "Home Page",

		"/machines": "Machine Lines Page",
		"/machines/:machineID": "Machine Details Page",
		"/machines/:machineID/:productid": "Product Details Page",

		"/recommendations": "Recommendations Page",
		"/cart": "Cart Page",
		"/checkout": "Checkout Page",

		"/finder": "Finder Page",

		"/order-received/:orderKey": "Quote Received Page",
		"/order-error/:orderKey": "Quote Error Page",
		"/order-history": "Quote History Page",
		"/bestellungen": "Quote History Page",
		"/order-history/:machineID/:productID": "Quote Product Details Page",
		"/order-history/:machineID": "Quote Details Page",
		"/track-order": "Track Quote Page",

		"/settings": "Settings Page",
		"/profile": "Profile Page",
		"/perfil": "Profile Page",

		"/admin": "Admin Page",
		"/rspl": "RSPL Page",

		"/documentation": "Documentation Page",
		"/documents": "Documents Page",
		"/documentation/:machine_id": "Documentation Details Page",

		"/requests": "Requests Page",
		"/support": "Support Page",
		"/soporte": "Support Page",

		"/saved_for_later": "Saved For Later Page",
		"/notifications": "Notifications Page",

		"/analytics": "Analytics Page",
		"/analytics/user": "User Analytics Page",

		"/services": "Services Page",
		"/reports": "Reports Page",
		"/search-results": "Search Results Page",

		"/logout": "Logout Page",
		"/cerrar-sesión": "Logout Page",

		"/model-viewer": "3D Model Viewer Page",
		"/2d-model-viewer": "2D Model Viewer Page",
		"/public/model-check": "Public Model Check Page",

		"/help": "Help Page",
		"/parts": "Parts Page",
	};
	return routeMap[normalizedPath] || path;
};

const getAdminTabLabel = (path = "") => {
	const [routePath = "/", queryString = ""] = String(path).split("#")[0].split("?");
	const params = new URLSearchParams(queryString);
	const normalizedPath = routePath || "/";

	if (!normalizedPath.startsWith("/admin")) {
		return getPageLabel(path);
	}

	const tabSlug = String(params.get("tab") || "").trim().toLowerCase();
	const adminTabLabelMap = {
		users: "Users",
		roles: "Roles",
		sites: "Sites",
		lines: "Lines",
		documents: "Documents",
		machine_provision: "Machine Provision",
		upload_models: "Upload Models",
		change_parts: "Change Parts",
		scheduler: "Scheduler",
		all_customers: "All Customers",
		all_sites: "All Sites",
		all_machines: "All Machines",
		all_products: "All Products",
		settings: "Settings",
		company_information: "Company Information",
		theme: "Theme",
		policy_center: "Policy Center",
		version_info: "Version Info",
	};

	return adminTabLabelMap[tabSlug] || "Admin Page";
};

const formatApiDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const normalizeDateValue = (value) => {
	if (!value) return null;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}
	if (typeof value?.toDate === "function") {
		const parsedDate = value.toDate();
		return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
	}
	const parsedDate = new Date(value);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDisplayDate = (date) =>
	date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : "";

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const getStartOfDay = (date) => {
	const next = new Date(date);
	next.setHours(0, 0, 0, 0);
	return next;
};

const getEndOfDay = (date) => {
	const next = new Date(date);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfWeek = (date) => {
	const next = getStartOfDay(date);
	const day = next.getDay();
	const diff = next.getDate() - day + (day === 0 ? -6 : 1);
	next.setDate(diff);
	return next;
};

const getEndOfWeek = (date) => {
	const next = getStartOfWeek(date);
	next.setDate(next.getDate() + 6);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfMonth = (date) => {
	const next = getStartOfDay(date);
	next.setDate(1);
	return next;
};

const getEndOfMonth = (date) => {
	const next = getStartOfMonth(date);
	next.setMonth(next.getMonth() + 1);
	next.setDate(0);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfQuarter = (date) => {
	const next = getStartOfDay(date);
	const currentMonth = next.getMonth();
	const startMonth = currentMonth <= 2 ? 0 : currentMonth <= 5 ? 3 : currentMonth <= 8 ? 6 : 9;
	next.setMonth(startMonth);
	next.setDate(1);
	return next;
};

const getEndOfQuarter = (date) => {
	const next = getStartOfQuarter(date);
	next.setMonth(next.getMonth() + 3);
	next.setDate(0);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfYear = (date) => {
	const next = getStartOfDay(date);
	next.setMonth(0);
	next.setDate(1);
	return next;
};

const getEndOfYear = (date) => {
	const next = getStartOfYear(date);
	next.setMonth(11);
	next.setDate(31);
	next.setHours(23, 59, 59, 999);
	return next;
};

const TrendIndicator = ({ trend }) => {
	if (!trend) return null;

	const direction = trend.direction || "flat";
	const isUp = direction === "up";
	const isDown = direction === "down";
	const Icon = isUp ? TrendingUpIcon : isDown ? TrendingDownIcon : TrendingFlatIcon;
	const color = isUp ? "success.main" : isDown ? "error.main" : "text.secondary";
	const change = Number(trend.change || 0);
	const changeLabel = `${change > 0 ? "+" : ""}${formatNumber(change)}%`;

	return (
		<Stack direction="row" alignItems="center" spacing={0.35} sx={{ color }}>
			<Icon sx={{ fontSize: 20 }} />
			<Typography variant="caption" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
				{changeLabel}
			</Typography>
		</Stack>
	);
};

const getRangeForKey = (key) => {
	const currentDate = new Date();

	if (key === "T") {
		return {
			key,
			label: "Today",
			startDate: getStartOfDay(currentDate),
			endDate: getEndOfDay(currentDate),
		};
	}

	if (key === "M") {
		return {
			key,
			label: "Month",
			startDate: getStartOfMonth(currentDate),
			endDate: getEndOfMonth(currentDate),
		};
	}

	if (key === "Q") {
		return {
			key,
			label: "Quarter",
			startDate: getStartOfQuarter(currentDate),
			endDate: getEndOfQuarter(currentDate),
		};
	}

	if (key === "Y") {
		return {
			key,
			label: "Year",
			startDate: getStartOfYear(currentDate),
			endDate: getEndOfYear(currentDate),
		};
	}

	return {
		key: "W",
		label: "Week",
		startDate: getStartOfWeek(currentDate),
		endDate: getEndOfWeek(currentDate),
	};
};

const MetricCard = ({ label, value, helper, icon, trend, compact = false, loading = false, onClick }) => (
	<Paper
		elevation={0}
		onClick={onClick}
		sx={{
			py: compact ? 1.75 : 2,
			px: compact ? 2.25 : 3.75,
			height: "100%",
			minHeight: compact ? 96 : 118,
			borderRadius: "10px",
			boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
			borderBottom: "4px solid transparent",
			backgroundColor: "background.paper",
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			gap: 2,
			transition: "all 0.3s ease",
			cursor: onClick ? "pointer" : "default",
			"&:hover": {
				boxShadow: "0 0 10px 2px rgba(0,0,0,0.16)",
				borderBottomColor: "primary.main",
			},
		}}
	>
		<Box sx={{ minWidth: 0, flex: "1 1 auto", alignSelf: "stretch", display: "flex", flexDirection: "column" }}>
			<Typography
				variant="h6"
				color="text.primary"
				sx={{ lineHeight: 1.25, fontWeight: 500, mb: 1 }}
			>
				{label}
			</Typography>
			{helper ? (
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
					{helper}
				</Typography>
			) : null}
			{loading ? (
				<Box sx={{ width: compact ? 96 : 132, mt: "auto", mb: 0.35 }}>
					<ChartLoading compact />
				</Box>
			) : (
				<Typography
					variant="h5"
					color="primary"
					sx={{ fontWeight: 600, lineHeight: 1.05, mt: "auto" }}
				>
					{value}
				</Typography>
			)}
		</Box>
		<Stack
			alignItems="flex-end"
			justifyContent="space-between"
			spacing={1}
			sx={{ alignSelf: "stretch", flex: "0 0 auto" }}
		>
			<Box
				sx={{
					width: compact ? 34 : 40,
					height: compact ? 34 : 40,
					borderRadius: 1.5,
					display: "grid",
					placeItems: "center",
					color: "text.secondary",
					bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
				}}
			>
				{icon}
			</Box>
			{!loading && onClick ? (
				<Box
					sx={{
						color: "text.secondary",
						lineHeight: 1,
						mt: -0.25,
					}}
				>
					<MoreHorizIcon fontSize="small" />
				</Box>
			) : null}
			{loading ? (
				<Box sx={{ width: 70 }}>
					<ChartLoading compact />
				</Box>
			) : (
				<TrendIndicator trend={trend} />
			)}
		</Stack>
	</Paper>
);

const UserAnalytics = () => {
	const { user, hasPermission } = useAuth();
	const { selectedSite, selectedSiteData, selectedSiteKey } = useSite();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = useMemo(() => new Set(resolveEnabledAdminModules(optionKeys)), [optionKeys]);
	const canSeeAnalytics = canAccessUserAnalytics(hasPermission, user, enabledAdminModuleSet);

	const [loading, setLoading] = useState(true);
	const [hasLoadedDashboard, setHasLoadedDashboard] = useState(false);
	const [error, setError] = useState("");
	const [dashboard, setDashboard] = useState({
		summary: {},
		topPages: [],
		topUsers: [],
		drilldowns: {
			activeUsers: [],
			adminActions: [],
			model2dMachines: [],
		},
		conversionFunnel: [],
		conversionSankey: {
			nodes: [],
			links: [],
		},
		trends: {},
		meta: {},
	});
	const [selectedRange, setSelectedRange] = useState(() => getRangeForKey("W"));
	const [selectedDateRange, setSelectedDateRange] = useState(() => {
		const initialRange = getRangeForKey("W");
		return [initialRange.startDate, initialRange.endDate];
	});
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [users, setUsers] = useState([]);
	const [machines, setMachines] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [usersOptions, setUsersOptions] = useState([]);
	const [machinesOptions, setMachinesOptions] = useState([]);
	const [filterLoading, setFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");
	const [accessScope, setAccessScope] = useState({ allowAll: false, allowNone: false });
	const [customerInput, setCustomerInput] = useState("");
	const [siteInput, setSiteInput] = useState("");
	const [userInput, setUserInput] = useState("");
	const [machineInput, setMachineInput] = useState("");
	const [filters, setFilters] = useState({
		customer_id: ALL_VALUE,
		site_id: ALL_VALUE,
		user_email: ALL_VALUE,
		machine_id: ALL_VALUE,
	});
	const [detailModal, setDetailModal] = useState({
		open: false,
		key: "",
		title: "",
		description: "",
		columns: [],
		rows: [],
		rowIdField: "id",
	});
	const quickRangeClickRef = useRef(false);
	const dashboardQueryRef = useRef("");
	const dashboardRequestIdRef = useRef(0);
	const isRefreshingDashboard = loading && hasLoadedDashboard;

	const baseSites = useMemo(() => {
		if (accessScope.allowNone) return [];
		if (accessScope.allowAll) return sites;
		return sites;
	}, [accessScope.allowAll, accessScope.allowNone, sites]);

	const baseCustomers = useMemo(() => {
		if (baseSites.length === 0) return accessScope.allowAll ? customers : [];
		if (accessScope.allowAll && baseSites.length === sites.length) return customers;
		const customerIds = new Set(baseSites.map(getSiteCustomerId).filter(Boolean));
		return customers.filter((customer) => customerIds.has(getCustomerId(customer)));
	}, [accessScope.allowAll, baseSites, customers, sites.length]);

	const getCustomerOptionsForSites = (scopedSites) => {
		const customerIds = new Set(scopedSites.map(getSiteCustomerId).filter(Boolean));
		const scopedCustomers =
			scopedSites.length === baseSites.length
				? baseCustomers
				: baseCustomers.filter((customer) => customerIds.has(getCustomerId(customer)));
		return getCustomerOptions(scopedCustomers).filter((option) => option.value);
	};

	const getSiteOptionsForSites = (scopedSites) =>
		getSiteOptions(scopedSites).filter((option) => option.value);

	const getMachineOptionsForSites = (scopedSites) => {
		const sitePairs = new Set(scopedSites.map(getSiteSelectionValue).filter(Boolean));
		const scopedMachines =
			scopedSites.length === baseSites.length
				? machines
				: machines.filter((machine) => {
					const siteId = getMachineSiteId(machine);
					const customerId = getMachineCustomerId(machine);
					if (!siteId) return false;
					return sitePairs.has(customerId ? `${siteId}:${customerId}` : String(siteId));
				});
		return getMachineOptions(scopedMachines).filter((option) => option.value);
	};

	const areFiltersEqual = (a, b) =>
		a.customer_id === b.customer_id &&
		a.site_id === b.site_id &&
		a.user_email === b.user_email &&
		a.machine_id === b.machine_id;

	const getSitesForMachine = (machineId) => {
		const parsedId = Number(machineId);
		if (!Number.isFinite(parsedId)) return [];
		const machineSites = machines
			.filter((machine) => getMachineId(machine) === parsedId)
			.map((machine) => {
				const siteId = getMachineSiteId(machine);
				const customerId = getMachineCustomerId(machine);
				return siteId ? { siteId, customerId } : null;
			})
			.filter(Boolean);
		return baseSites.filter((site) => {
			const siteId = getSiteId(site);
			const customerId = getSiteCustomerId(site);
			return machineSites.some(
				(machineSite) =>
					machineSite.siteId === siteId &&
					(!machineSite.customerId || !customerId || machineSite.customerId === customerId)
			);
		});
	};

	const computeScopedSites = (draftFilters, excludedKey = "") => {
		let scopedSites = baseSites;
		if (excludedKey !== "site_id" && !isAllValue(draftFilters.site_id)) {
			const siteId = getFilterSiteId(draftFilters.site_id);
			const customerId = getFilterSiteCustomerId(draftFilters.site_id);
			scopedSites = scopedSites.filter(
				(site) =>
					getSiteId(site) === siteId &&
					(!customerId || getSiteCustomerId(site) === customerId)
			);
		}
		if (excludedKey !== "customer_id" && !isAllValue(draftFilters.customer_id)) {
			const customerId = Number(draftFilters.customer_id);
			scopedSites = scopedSites.filter((site) => getSiteCustomerId(site) === customerId);
		}
		if (excludedKey !== "machine_id" && !isAllValue(draftFilters.machine_id)) {
			const sitesForMachine = getSitesForMachine(draftFilters.machine_id);
			const machineSiteIds = new Set(sitesForMachine.map(getSiteId).filter(Boolean));
			scopedSites = scopedSites.filter((site) => machineSiteIds.has(getSiteId(site)));
		}
		if (excludedKey !== "user_email" && !isAllValue(draftFilters.user_email)) {
			const selectedUser = users.find(
				(userRow) => normalizeEmail(userRow.user_email) === normalizeEmail(draftFilters.user_email)
			);
			if (!selectedUser) {
				return [];
			}
			const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
			const allowedSiteIds = new Set(userSiteIds);
			scopedSites = scopedSites.filter((site) => allowedSiteIds.has(getSiteId(site)));
		}
		return scopedSites;
	};

	const buildOptionsForFilters = (draftFilters) => {
		const customerScopedSites = computeScopedSites(draftFilters, "customer_id");
		const siteScopedSites = computeScopedSites(draftFilters, "site_id");
		const machineScopedSites = computeScopedSites(draftFilters, "machine_id");
		const userScopedSites = computeScopedSites(draftFilters, "user_email");
		return {
			customerOptions: getCustomerOptionsForSites(customerScopedSites),
			siteOptions: getSiteOptionsForSites(siteScopedSites),
			machineOptions: getMachineOptionsForSites(machineScopedSites),
			userOptions: getUserOptionsForScope(userScopedSites, draftFilters),
		};
	};

	const getUserOptionsForScope = (scopedSites, draftFilters) => {
		const dropdownUsers = users.filter((userRow) => !isSuperAdminRow(userRow));
		const adminUsers = dropdownUsers.filter((userRow) => {
			const allocatedIds = getUserAllocatedSiteIds(userRow);
			const allocatedCustomerIds = getUserAllocatedCustomerIds(userRow);
			return allocatedIds.length === 0 && allocatedCustomerIds.length === 0 && isAdminRow(userRow);
		});
		const adminEmails = new Set(adminUsers.map((userRow) => normalizeEmail(userRow.user_email)));
		const nonAdminUsers = dropdownUsers.filter(
			(userRow) => !adminEmails.has(normalizeEmail(userRow.user_email))
		);
		const siteIdsForScope = new Set(scopedSites.map(getSiteId).filter(Boolean));
		const selectedSiteId = isAllValue(draftFilters.site_id) ? null : getFilterSiteId(draftFilters.site_id);
		const selectedSiteCustomerId = isAllValue(draftFilters.site_id)
			? null
			: getFilterSiteCustomerId(draftFilters.site_id);
		const selectedCustomerId = isAllValue(draftFilters.customer_id)
			? null
			: Number(draftFilters.customer_id);
		const customerSiteIds = selectedCustomerId
			? baseSites
				.filter((site) => getSiteCustomerId(site) === selectedCustomerId)
				.map(getSiteId)
				.filter(Boolean)
			: [];
		const requiredSiteIds = selectedSiteId
			? new Set([selectedSiteId])
			: selectedCustomerId
				? new Set(customerSiteIds)
				: siteIdsForScope;
		const isFullScope =
			isAllValue(draftFilters.customer_id) &&
			isAllValue(draftFilters.site_id) &&
			isAllValue(draftFilters.machine_id);
		const scopedUsers = isFullScope
			? nonAdminUsers
			: nonAdminUsers.filter((userRow) => {
				const userSiteIds = getAccessibleSiteIdsForUser(userRow, baseSites);
				if (userSiteIds.length === 0) return false;
				return userSiteIds.some((siteId) => requiredSiteIds.has(Number(siteId)));
			});
		const customerScopedUsers = selectedSiteCustomerId
			? scopedUsers.filter((userRow) => {
				const customerIds = getUserAllocatedCustomerIds(userRow);
				return customerIds.length === 0 || customerIds.includes(selectedSiteCustomerId);
			})
			: scopedUsers;
		const includeAdminUsers = accessScope.allowAll && (isFullScope || requiredSiteIds.size > 0);
		const combined = includeAdminUsers
			? [...adminUsers, ...customerScopedUsers]
			: customerScopedUsers;
		return getUserOptions(combined);
	};

	const validateSelections = (draftFilters, options) => {
		const isValid = (value, list) =>
			isAllValue(value) || list.some((option) => String(option.value) === String(value));
		const nextFilters = { ...draftFilters };
		if (!isValid(nextFilters.customer_id, options.customerOptions)) nextFilters.customer_id = ALL_VALUE;
		if (!isValid(nextFilters.site_id, options.siteOptions)) nextFilters.site_id = ALL_VALUE;
		if (!isValid(nextFilters.user_email, options.userOptions)) nextFilters.user_email = ALL_VALUE;
		if (!isValid(nextFilters.machine_id, options.machineOptions)) nextFilters.machine_id = ALL_VALUE;
		return nextFilters;
	};

	const applyAutoSelection = (draftFilters) => {
		const nextFilters = { ...draftFilters };
		if (!isAllValue(nextFilters.site_id)) {
			const selectedFilterSiteId = getFilterSiteId(nextFilters.site_id);
			const selectedFilterCustomerId = getFilterSiteCustomerId(nextFilters.site_id);
			const selectedSiteRow = baseSites.find(
				(site) =>
					getSiteId(site) === selectedFilterSiteId &&
					(!selectedFilterCustomerId || getSiteCustomerId(site) === selectedFilterCustomerId)
			);
			if (!selectedSiteRow) {
				nextFilters.site_id = ALL_VALUE;
			} else if (isAllValue(nextFilters.customer_id)) {
				nextFilters.customer_id = getSiteCustomerId(selectedSiteRow) ?? nextFilters.customer_id;
			}
		}
		if (!isAllValue(nextFilters.customer_id) && !isAllValue(nextFilters.site_id)) {
			const selectedFilterSiteId = getFilterSiteId(nextFilters.site_id);
			const selectedFilterCustomerId = getFilterSiteCustomerId(nextFilters.site_id);
			const selectedSiteRow = baseSites.find(
				(site) =>
					getSiteId(site) === selectedFilterSiteId &&
					(!selectedFilterCustomerId || getSiteCustomerId(site) === selectedFilterCustomerId)
			);
			if (selectedSiteRow && getSiteCustomerId(selectedSiteRow) !== Number(nextFilters.customer_id)) {
				nextFilters.site_id = ALL_VALUE;
			}
		}
		if (!isAllValue(nextFilters.machine_id)) {
			const candidateSites = getSitesForMachine(nextFilters.machine_id);
			const candidateSitePairs = new Set(candidateSites.map(getSiteSelectionValue).filter(Boolean));
			if (candidateSites.length === 0) {
				nextFilters.machine_id = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site_id)) {
				if (!candidateSitePairs.has(String(nextFilters.site_id))) {
					nextFilters.machine_id = ALL_VALUE;
				}
			} else if (!isAllValue(nextFilters.customer_id)) {
				const customerCandidates = candidateSites.filter(
					(site) => getSiteCustomerId(site) === Number(nextFilters.customer_id)
				);
				if (customerCandidates.length === 1) {
					nextFilters.site_id = getSiteSelectionValue(customerCandidates[0]);
					nextFilters.customer_id = getSiteCustomerId(customerCandidates[0]) ?? nextFilters.customer_id;
				}
			} else if (candidateSites.length === 1) {
				nextFilters.site_id = getSiteSelectionValue(candidateSites[0]);
				nextFilters.customer_id = getSiteCustomerId(candidateSites[0]) ?? nextFilters.customer_id;
			}
		}
		if (!isAllValue(nextFilters.user_email)) {
			const selectedUser = users.find(
				(userRow) => normalizeEmail(userRow.user_email) === normalizeEmail(nextFilters.user_email)
			);
			if (!selectedUser) {
				nextFilters.user_email = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site_id)) {
				const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
				if (!userSiteIds.includes(getFilterSiteId(nextFilters.site_id))) {
					nextFilters.user_email = ALL_VALUE;
				}
			}
		}
		return nextFilters;
	};

	const computeNextState = (inputFilters) => {
		let draftFilters = { ...inputFilters };
		for (let index = 0; index < 2; index += 1) {
			draftFilters = applyAutoSelection(draftFilters);
			const options = buildOptionsForFilters(draftFilters);
			const validatedFilters = validateSelections(draftFilters, options);
			if (areFiltersEqual(draftFilters, validatedFilters)) {
				return { filters: validatedFilters, options };
			}
			draftFilters = validatedFilters;
		}
		const options = buildOptionsForFilters(draftFilters);
		return { filters: draftFilters, options };
	};

	useEffect(() => {
		if (!baseSites.length) return;
		if (selectedSiteData?.site_id && Number(selectedSiteData.site_id) === 0) {
			setFilters((prevFilters) => ({
				...prevFilters,
				customer_id: ALL_VALUE,
				site_id: ALL_VALUE,
			}));
			return;
		}
		const selectedSiteRow =
			(selectedSiteData?.site_id
				? baseSites.find(
						(site) =>
							getSiteId(site) === Number(selectedSiteData.site_id) &&
							(!selectedSiteData?.cus_id ||
								getSiteCustomerId(site) === Number(selectedSiteData.cus_id))
				  )
				: null) ||
			(selectedSiteKey
				? baseSites.find((site) => getSiteSelectionValue(site) === selectedSiteKey)
				: null) ||
			(selectedSite
				? baseSites.find((site) => String(site.customer_site_number) === String(selectedSite))
				: null);
		if (!selectedSiteRow) return;
		const nextSiteId = getSiteSelectionValue(selectedSiteRow);
		const nextCustomerId = getSiteCustomerId(selectedSiteRow) || ALL_VALUE;
		setFilters((prevFilters) => {
			if (
				String(prevFilters.site_id) === String(nextSiteId) &&
				String(prevFilters.customer_id) === String(nextCustomerId)
			) {
				return prevFilters;
			}
			return {
				...prevFilters,
				customer_id: nextCustomerId,
				site_id: nextSiteId,
			};
		});
	}, [baseSites, selectedSite, selectedSiteData, selectedSiteKey]);

	useEffect(() => {
		const { filters: nextFilters, options } = computeNextState(filters);
		setCustomersOptions(withAllOption(options.customerOptions));
		setSitesOptions(withAllOption(options.siteOptions));
		setUsersOptions(withAllOption(options.userOptions));
		setMachinesOptions(withAllOption(options.machineOptions));
		if (!areFiltersEqual(filters, nextFilters)) {
			setFilters(nextFilters);
		}
	}, [baseSites, baseCustomers, machines, users, filters]);

	const filterOptionMap = useMemo(
		() => ({
			customer_id: customersOptions,
			site_id: sitesOptions,
			user_email: usersOptions,
			machine_id: machinesOptions,
		}),
		[customersOptions, machinesOptions, sitesOptions, usersOptions]
	);

	useEffect(() => {
		const syncInput = (key, setInput) => {
			const value = filters[key];
			if (isAllValue(value)) {
				setInput("");
				return;
			}
			const selectedOption = (filterOptionMap[key] || []).find(
				(option) => String(option.value) === String(value)
			);
			if (selectedOption?.label) {
				setInput(selectedOption.label);
			}
		};

		syncInput("customer_id", setCustomerInput);
		syncInput("site_id", setSiteInput);
		syncInput("user_email", setUserInput);
		syncInput("machine_id", setMachineInput);
	}, [filterOptionMap, filters]);

	const buildDashboardQuery = (
		nextFilters = filters,
		nextRange = selectedRange,
		nextDateRange = selectedDateRange
	) => {
		const [rangeStart, rangeEnd] = Array.isArray(nextDateRange) ? nextDateRange : [];
		const selectedStartDate = normalizeDateValue(rangeStart);
		const selectedEndDate = normalizeDateValue(rangeEnd);
		const hasCompleteDateRange = selectedStartDate && selectedEndDate;
		const startDate = hasCompleteDateRange ? selectedStartDate : normalizeDateValue(nextRange.startDate);
		const endDate = hasCompleteDateRange ? selectedEndDate : normalizeDateValue(nextRange.endDate);
		const normalizedFilters = FILTER_KEYS.reduce((query, filter) => {
			const value = nextFilters[filter.key];
			query[filter.key] =
				filter.key === "site_id" && !isAllValue(value)
					? getFilterSiteId(value) || ""
					: isAllValue(value) ? "" : value;
			return query;
		}, {});
		const selectedCustomer = !isAllValue(nextFilters.customer_id)
			? customers.find((customer) => getCustomerId(customer) === Number(nextFilters.customer_id))
			: null;
		const selectedSite = !isAllValue(nextFilters.site_id)
			? sites.find((site) => {
				const siteId = getFilterSiteId(nextFilters.site_id);
				const customerId = getFilterSiteCustomerId(nextFilters.site_id);
				return (
					getSiteId(site) === siteId &&
					(!customerId || getSiteCustomerId(site) === customerId)
				);
			})
			: null;
		const selectedMachine = !isAllValue(nextFilters.machine_id)
			? machines.find((machine) => getMachineId(machine) === Number(nextFilters.machine_id))
			: null;

		return {
			...normalizedFilters,
			customer_name: selectedCustomer?.customer_name || "",
			site_name: selectedSite?.customer_site_name || selectedSite?.site_name || "",
			machine_name: selectedMachine?.machine_name || "",
			machine_number: selectedMachine?.machine_number || "",
			range:
				nextRange.key === "T"
					? "1d"
					: nextRange.key === "W"
						? "7d"
						: nextRange.key === "M"
							? "30d"
							: nextRange.key === "Q"
								? "90d"
								: nextRange.key === "Y"
									? "1y"
									: "7d",
			startDate: formatApiDate(startDate),
			endDate: formatApiDate(endDate),
		};
	};

	const loadDashboard = async (query = buildDashboardQuery()) => {
		const requestId = dashboardRequestIdRef.current + 1;
		dashboardRequestIdRef.current = requestId;
		setLoading(true);
		setError("");
		try {
			const result = await getUserAnalyticsDashboard(query);
			if (requestId !== dashboardRequestIdRef.current) return;
			if (result?.success === false) {
				setError(result?.message || "Unable to load analytics data.");
				return;
			}
			setDashboard(result || {});
			setHasLoadedDashboard(true);
		} catch (err) {
			if (requestId === dashboardRequestIdRef.current) {
				setError(err?.message || "Unable to load analytics data.");
			}
		} finally {
			if (requestId === dashboardRequestIdRef.current) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		const query = buildDashboardQuery(filters, selectedRange, selectedDateRange);
		const queryKey = JSON.stringify(query);
		if (queryKey === dashboardQueryRef.current) return;
		dashboardQueryRef.current = queryKey;
		loadDashboard(query);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, selectedRange, selectedDateRange, user?.user_id]);

	useEffect(() => {
		let isMounted = true;

		const loadFilterOptions = async () => {
			if (!user) return;
			setFilterLoading(true);
			setFilterError("");

			try {
				const allocationResponse = await getUserAllocations(user.user_id);
				const allocations = allocationResponse?.data?.allocations || [];
				const siteIds = [...new Set(allocations.map((allocation) => Number(allocation?.site_id)).filter(Boolean))];
				const customerIds = [...new Set(allocations.map((allocation) => Number(allocation?.cus_id)).filter(Boolean))];
				const hasAllocations = siteIds.length > 0 || customerIds.length > 0;
				const scope = {
					allowAll: !hasAllocations,
					allowNone: false,
				};
				const scopedSiteIdSet = new Set(siteIds);
				const scopedCustomerIdSet = new Set(customerIds);

				const [customerResponse, siteResponse, userResponse, machineResponse] = await Promise.all([
					getCustomersCusSites(),
					getAllCustomerSites(),
					getAllUsers(),
					getMachinesByProvision({ includeInactive: true }),
				]);
				if (!isMounted) return;
				const errors = [];
				let loadedCustomers = [];
				let allCustomerRows = [];
				let loadedSites = [];
				setAccessScope(scope);

				if (customerResponse?.status) {
					const allCustomers = (customerResponse.data || []).filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					const dedupedCustomers = uniqueBy(allCustomers, (customer) =>
						normalizeCustomerNumber(customer?.customer_number) || String(getCustomerId(customer))
					);
					allCustomerRows = dedupedCustomers;
					loadedCustomers = scope.allowAll
						? dedupedCustomers
						: scope.allowNone
							? []
							: dedupedCustomers.filter((customer) => scopedCustomerIdSet.has(getCustomerId(customer)));
					setCustomers(loadedCustomers);
				} else {
					setCustomers([]);
					errors.push("Unable to load customer filter options.");
				}

				if (siteResponse?.status) {
					const allSites = (siteResponse.data || []).filter(
						(site) =>
							getSiteId(site) &&
							(
								site.customer_site_name ||
								site.customer_site_number ||
								site.site_name ||
								site.customer_name ||
								site.parent_customer_name
							)
					);
					const dedupedSites = uniqueBy(allSites, (site) =>
						`${getSiteId(site) || normalizeSiteNumber(site?.customer_site_number || site?.site_number)}:${getSiteCustomerId(site) || ""}`
					);
					const customerById = new Map(
						loadedCustomers.map((customer) => [getCustomerId(customer), customer])
					);
					const enrichedSites = dedupedSites.map((site) => {
						const customer = customerById.get(getSiteCustomerId(site));
						return customer
							? {
								...site,
								customer_name: site.customer_name || customer.customer_name,
								customer_number: site.customer_number || customer.customer_number,
								parent_customer_name: site.parent_customer_name || customer.customer_name,
								parent_customer_number: site.parent_customer_number || customer.customer_number,
							}
							: site;
					});
					if (scope.allowAll) {
						loadedSites = enrichedSites;
					} else if (scope.allowNone) {
						loadedSites = [];
					} else if (siteIds.length > 0) {
						loadedSites = enrichedSites.filter((site) => scopedSiteIdSet.has(getSiteId(site)));
					} else {
						loadedSites = enrichedSites.filter((site) => scopedCustomerIdSet.has(getSiteCustomerId(site)));
					}
					setSites(loadedSites);
					if (!scope.allowAll) {
						const siteCustomerIds = new Set(loadedSites.map(getSiteCustomerId).filter(Boolean));
						loadedCustomers = scope.allowNone
							? []
							: allCustomerRows.filter((customer) => siteCustomerIds.has(getCustomerId(customer)));
						setCustomers(loadedCustomers);
					}
				} else {
					setSites([]);
					errors.push("Unable to load site filter options.");
				}

				if (Array.isArray(userResponse?.data)) {
					const scopedSiteIds = new Set(loadedSites.map(getSiteId).filter(Boolean));
					const dedupedUsers = uniqueBy(
						userResponse.data,
						(userRow) => normalizeEmail(userRow?.user_email) || String(userRow?.user_id || "")
					).filter((userRow) => !isSuperAdminRow(userRow));
					const scopedUsers = scope.allowAll
						? dedupedUsers
						: scope.allowNone
							? []
							: dedupedUsers.filter((userRow) => {
								if (!isApprovedUser(userRow) || isAdminRow(userRow)) return false;
								const userSiteIds = getAccessibleSiteIdsForUser(userRow, loadedSites);
								return userSiteIds.some((siteId) => scopedSiteIds.has(Number(siteId)));
							});
					setUsers(scopedUsers);
				} else {
					setUsers([]);
					errors.push("Unable to load user filter options.");
				}

				if (machineResponse?.status) {
					const scopedSiteIds = new Set(loadedSites.map(getSiteId).filter(Boolean));
					const loadedMachines = machineResponse.data || [];
					setMachines(
						scope.allowAll
							? loadedMachines
							: scope.allowNone
								? []
								: loadedMachines.filter((machine) => scopedSiteIds.has(getMachineSiteId(machine)))
					);
				} else {
					setMachines([]);
					errors.push("Unable to load machine filter options.");
				}

				if (errors.length > 0) {
					setFilterError(errors.join(" "));
				}
			} catch (err) {
				if (!isMounted) return;
				setCustomers([]);
				setSites([]);
				setUsers([]);
				setMachines([]);
				setAccessScope({ allowAll: false, allowNone: true });
				setFilterError(`Unable to load analytics filter options: ${err.message}`);
			} finally {
				if (isMounted) {
					setFilterLoading(false);
				}
			}
		};

		loadFilterOptions();

		return () => {
			isMounted = false;
		};
	}, [user]);

	const summaryCards = useMemo(
		() => {
			const rangeKey = selectedRange.key;
			const activeUsers = dashboard.meta?.rangeUsers;

			// Pick the trend that matches the selected range granularity.
			const activeTrend =
				rangeKey === "T"
					? dashboard.trends?.dau
					: rangeKey === "Y"
						? (dashboard.trends?.yau ?? dashboard.trends?.mau)
						: dashboard.trends?.mau;

			return [
				{
					label: "Active Users",
					value: formatNumber(activeUsers),
					helper: `${selectedRange.label} active users`,
					icon: <PeopleAltOutlinedIcon fontSize="small" />,
					trend: activeTrend,
					tone: "#2563eb",
					featured: true,
				},
				{
					label: "DAU / MAU",
					value: `${formatNumber(dashboard.summary?.stickiness)}%`,
					helper: "Stickiness",
					icon: <TimelineOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.stickiness,
					tone: "#16a34a",
					featured: true,
				},
				{
					label: "Average Session Duration",
					value: formatDuration(dashboard.summary?.averageSessionDuration),
					helper: "Time per active user",
					icon: <AccessTimeOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.averageSessionDuration,
					tone: "#7c3aed",
					featured: true,
				},
				{
					label: "Average Sessions / User",
					value: formatNumber(dashboard.summary?.averageSessionsPerUser),
					icon: <TimelineOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.averageSessionsPerUser,
					tone: "#0891b2",
				},
				{
					label: "Login Frequency / User",
					value: formatNumber(dashboard.summary?.loginFrequencyPerUser),
					icon: <LoginOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.loginFrequencyPerUser,
					tone: "#f97316",
				},
				{
					label: "Page Views / Session",
					value: formatNumber(dashboard.summary?.pageViewsPerSession),
					icon: <PageviewOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.pageViewsPerSession,
					tone: "#0f766e",
				},
				{
					label: "Page Views / User",
					value: formatNumber(dashboard.summary?.pageViewsPerUser),
					icon: <PageviewOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.pageViewsPerUser,
					tone: "#4f46e5",
				},
				{
					label: "Clicks",
					value: formatNumber(dashboard.summary?.clicks),
					icon: <MouseOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.clicks,
					tone: "#db2777",
				},
				{
					label: "2D Model Opens",
					value: formatNumber(dashboard.summary?.model2dOpens),
					icon: <DescriptionOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.model2dOpens,
					tone: "#0d9488",
				},
				// {
				// 	label: "3D Model Opens",
				// 	value: formatNumber(dashboard.summary?.model3dOpens),
				// 	icon: <ViewInArOutlinedIcon fontSize="small" />,
				// 	trend: dashboard.trends?.model3dOpens,
				// 	tone: "#0d9488",
				// },
				{
					label: "Checkout Steps",
					value: formatNumber(dashboard.summary?.checkoutSteps),
					icon: <ShoppingCartCheckoutOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.checkoutSteps,
					tone: "#ea580c",
				},
				{
					label: "Admin Actions",
					value: formatNumber(dashboard.summary?.adminActions),
					icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.adminActions,
					tone: "#475569",
				},
			];
		},
		[dashboard.meta, dashboard.summary, dashboard.trends, selectedRange.key, selectedRange.label]
	);

	const detailCardMap = useMemo(
		() => ({
			"Active Users": {
				title: `Active Users`,
				description: `Active users in the selected range.`,
				columns: [
					{ field: "user_name", headerName: "User", flex: 1.1, minWidth: 180 },
					{ field: "user_email", headerName: "Email", flex: 1.2, minWidth: 220 },
				],
				rows: (dashboard.drilldowns?.activeUsers || []).map((row, index) => ({
					...row,
					__rowId: row.user_id || row.user_email || `active-user-${index}`,
				})),
				rowIdField: "__rowId",
			},
			"Admin Actions": {
				title: `Admin Actions`,
				description: `Admin actions are grouped page wise.`,
				columns: [
					{ field: "tabLabel", headerName: "Tab", flex: 1.1, minWidth: 220, valueGetter: (_, row) => getAdminTabLabel(row.path) },
					{ field: "actions", headerName: "Actions", type: "number", width: 110 },
					{ field: "users", headerName: "Users", type: "number", width: 100 },
				],
				rows: (dashboard.drilldowns?.adminActions || []).map((row, index) => ({
					...row,
					__rowId: row.path || `admin-action-${index}`,
				})),
				rowIdField: "__rowId",
			},
			"Clicks": {
				title: `Clicks`,
				description: `Clicks are grouped by target.`,
				columns: [
					{ field: "target", headerName: "Click Target", flex: 1, minWidth: 220 },
					{ field: "action_category", headerName: "Category", flex: 0.9, minWidth: 180 },
					{ field: "path", headerName: "Path", flex: 1.2, minWidth: 260 },
					{ field: "clicks", headerName: "Clicks", type: "number", width: 100 },
					{ field: "users", headerName: "Users", type: "number", width: 100 },
				],
				rows: (dashboard.drilldowns?.clicks || []).map((row, index) => ({
					...row,
					__rowId: `${row.path || "path"}-${row.target || "target"}-${index}`,
				})),
				rowIdField: "__rowId",
			},
			"2D Model Opens": {
				title: ` 2D Model Opens`,
				description: `2D Model opens are grouped machine wise.`,
				columns: [
					{ field: "machine_name", headerName: "Machine", flex: 1, minWidth: 180 },
					{ field: "machine_number", headerName: "Machine Number", flex: 0.9, minWidth: 160 },
					{ field: "site_name", headerName: "Site", flex: 1, minWidth: 180 },
					{ field: "customer_name", headerName: "Customer", flex: 1, minWidth: 180 },
					{ field: "opens", headerName: "Opens", type: "number", width: 100 },
					{ field: "users", headerName: "Users", type: "number", width: 100 },
				],
				rows: (dashboard.drilldowns?.model2dMachines || []).map((row, index) => ({
					...row,
					__rowId: row.machine_id || row.machine_number || row.machine_name || `2d-machine-${index}`,
				})),
				rowIdField: "__rowId",
			},
		}),
		[dashboard.drilldowns, dashboard.summary, selectedRange.label]
	);

	const handleMetricCardClick = (label) => {
		const config = detailCardMap[label];
		if (!config) return;
		setDetailModal({
			open: true,
			key: label,
			title: config.title,
			description: config.description,
			columns: config.columns,
			rows: config.rows,
			rowIdField: config.rowIdField || "id",
		});
	};

	const handleCloseDetailModal = () => {
		setDetailModal({
			open: false,
			key: "",
			title: "",
			description: "",
			columns: [],
			rows: [],
			rowIdField: "id",
		});
	};
	const featuredSummaryCards = useMemo(() => summaryCards.filter((card) => card.featured), [summaryCards]);
	const activitySummaryCards = useMemo(() => summaryCards.filter((card) => !card.featured), [summaryCards]);
	const documentSummaryCards = useMemo(
		() => [
			{
				label: "Document Downloads",
				value: formatNumber(dashboard.summary?.downloads),
				helper: "Downloaded documents",
				icon: <DownloadOutlinedIcon fontSize="small" />,
				trend: dashboard.trends?.downloads,
				tone: "#9333ea",
			},
			{
				label: "Recently Viewed",
				value: formatNumber(dashboard.summary?.recentlyViewedDocuments),
				helper: "Viewed documents",
				icon: <HistoryOutlinedIcon fontSize="small" />,
				trend: dashboard.trends?.recentlyViewedDocuments,
				tone: "#0d9488",
			},
			{
				label: "Favourites",
				value: formatNumber(dashboard.summary?.favoriteDocuments),
				helper: "Saved documents",
				icon: <StarBorderOutlinedIcon fontSize="small" />,
				trend: dashboard.trends?.favoriteDocuments,
				tone: "#f59e0b",
			},
		],
		[dashboard.summary, dashboard.trends]
	);

	const funnelSankeyOption = useMemo(
		() => ({
			tooltip: {
				trigger: "item",
				triggerOn: "mousemove",
				valueFormatter: (value) => `${formatNumber(value)} sessions`,
			},
			series: [
				{
					type: "sankey",
					data: dashboard.conversionSankey?.nodes || [],
					links: dashboard.conversionSankey?.links || [],
					nodeWidth: 16,
					nodeGap: 14,
					right: 190,
					left: 16,
					top: 12,
					bottom: 12,
					emphasis: {
						focus: "adjacency",
					},
					lineStyle: {
						color: "gradient",
						curveness: 0.45,
					},
					label: {
						color: "#333",
						fontSize: 12,
						lineHeight: 16,
						overflow: "break",
						width: 170,
					},
				},
			],
			media: [
				{
					query: { maxWidth: 640 },
					option: {
						series: [
							{
								right: 120,
								left: 8,
								label: {
									width: 105,
								},
							},
						],
					},
				},
			],
		}),
		[dashboard.conversionSankey]
	);

	const handleFilterChange = (key, value) => {
		const nextValue = isAllValue(value) ? ALL_VALUE : value;
		if (key === "customer_id") {
			setSiteInput("");
			setUserInput("");
			setMachineInput("");
		}
		const selectedSite =
			key === "site_id" && !isAllValue(nextValue)
				? sites.find((site) => String(getSiteSelectionValue(site)) === String(nextValue))
				: null;
		const selectedSiteCustomerId = selectedSite ? getSiteCustomerId(selectedSite) : null;
		if (selectedSiteCustomerId) {
			const selectedCustomer = customers.find(
				(customer) => String(getCustomerId(customer)) === String(selectedSiteCustomerId)
			);
			if (selectedCustomer) {
				setCustomerInput(`${selectedCustomer.customer_name} - ${selectedCustomer.customer_number}`);
			}
		}
		setFilters((prevFilters) => {
			const nextFilters = {
				...prevFilters,
				[key]: nextValue,
			};

			if (key === "customer_id") {
				nextFilters.site_id = ALL_VALUE;
				nextFilters.user_email = ALL_VALUE;
				nextFilters.machine_id = ALL_VALUE;
			}

			if (key === "site_id" && !isAllValue(nextValue)) {
				if (selectedSiteCustomerId) {
					nextFilters.customer_id = selectedSiteCustomerId;
				}
			}

			return nextFilters;
		});
	};

	const handleDateRangeClick = (key) => {
		const nextRange = getRangeForKey(key);
		quickRangeClickRef.current = true;
		setSelectedRange(nextRange);
		setSelectedDateRange([nextRange.startDate, nextRange.endDate]);
	};
	const aggregatedTopPages = useMemo(() => {
		const grouped = {};

		(dashboard.topPages || []).forEach((row) => {
			const pageLabel = getPageLabel(row.path);

			if (!grouped[pageLabel]) {
				grouped[pageLabel] = {
					page: pageLabel,
					views: 0,
				};
			}

			grouped[pageLabel].views += Number(row.views || 0);
		});

		const totalViews = Object.values(grouped).reduce((sum, row) => sum + row.views, 0);

		return Object.values(grouped)
			.map((row) => ({
				...row,
				percentage: totalViews ? ((row.views / totalViews) * 100).toFixed(2) : 0,
			}))
			.sort((a, b) => b.views - a.views);
	}, [dashboard.topPages]);

	const handleDateRangePickerChange = (nextDateRange) => {
		const normalizedDateRange = Array.isArray(nextDateRange)
			? nextDateRange.map((value) => normalizeDateValue(value))
			: null;
		setSelectedDateRange(normalizedDateRange);

		if (quickRangeClickRef.current) {
			quickRangeClickRef.current = false;
			return;
		}

		if (!Array.isArray(normalizedDateRange)) return;

		const [rawStartDate, rawEndDate] = normalizedDateRange;
		if (!rawStartDate || !rawEndDate) return;

		const startDate = getStartOfDay(rawStartDate);
		const normalizedEndDate = startDate > rawEndDate ? new Date(startDate) : new Date(rawEndDate);
		normalizedEndDate.setHours(23, 59, 59, 999);
		const normalizedStartDate = startDate > rawEndDate ? getStartOfDay(rawEndDate) : startDate;
		setSelectedDateRange([normalizedStartDate, normalizedEndDate]);

		const nextRange = {
			key: "C",
			label: "Custom",
			startDate: normalizedStartDate,
			endDate: normalizedEndDate,
		};

		setSelectedRange(nextRange);
	};
	const handleReset = () => {
		setFilters({
			customer_id: ALL_VALUE,
			site_id: ALL_VALUE,
			user_email: ALL_VALUE,
			machine_id: ALL_VALUE,
		});
		setCustomerInput("");
		setSiteInput("");
		setUserInput("");
		setMachineInput("");
	};

	const handleClearFilter = (key) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			[key]: ALL_VALUE,
			...(key === "customer_id"
				? { site_id: ALL_VALUE, user_email: ALL_VALUE, machine_id: ALL_VALUE }
				: {}),
		}));
		if (key === "customer_id") setCustomerInput("");
		if (key === "site_id" || key === "customer_id") setSiteInput("");
		if (key === "user_email" || key === "customer_id") setUserInput("");
		if (key === "machine_id" || key === "customer_id") setMachineInput("");
	};

	const appliedFilters = useMemo(
		() =>
			FILTER_KEYS
				.filter((filter) => !isAllValue(filters[filter.key]))
				.map((filter) => {
					const selectedOption = (filterOptionMap[filter.key] || []).find(
						(option) => String(option.value) === String(filters[filter.key])
					);
					return {
						...filter,
						value: filters[filter.key],
						display: `${filter.label}: ${selectedOption?.label || filters[filter.key]}`,
					};
				}),
		[filterOptionMap, filters]
	);

	const filterInputMap = {
		customer_id: customerInput,
		site_id: siteInput,
		user_email: userInput,
		machine_id: machineInput,
	};

	const handleFilterInputChange = (key, value) => {
		if (key === "customer_id") setCustomerInput(value);
		if (key === "site_id") setSiteInput(value);
		if (key === "user_email") setUserInput(value);
		if (key === "machine_id") setMachineInput(value);
	};

	const getDisplayedFilterOptions = (key, options, selectedOption) => {
		const query = getSearchQuery(filterInputMap[key], selectedOption);
		const filtered = query
			? options.filter((option) =>
				normalizeSearchValue(`${option.searchText || ""} ${option.label || ""}`).includes(query)
			)
			: options;
		return filtered.slice(0, OPTION_LIMIT);
	};

	const renderContentLoader = (minHeight = 220) => (
		<Box sx={{ minHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
			<ChartLoading />
		</Box>
	);

	if (!canSeeAnalytics) {
		return <NotAuthorized />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: "100vw",
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				py: 3,
				px: { xs: 2, md: 5 },
				overflowX: "hidden",
			}}
		>
			<PageTitle title="User Analytics" />

			{/* <Box sx={{ mb: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 0.75 }}>
					User Analytics
				</Typography>
				<Typography variant="body2" color="text.secondary">
					OpenPanel-backed KPI dashboard for product adoption, usage, and page traffic.
				</Typography>
			</Box> */}

			<Paper
				elevation={0}
				sx={{
					px: { xs: 0, md: 0 },
					py: { xs: 1, md: 1.5 },
					mb: 2,
					border: 0,
					borderRadius: 0,
					background: "inherit",
					boxShadow: "none",
					position: { xs: "static", md: "sticky" },
					top: { md: 75 },
					zIndex: 1051,
				}}
			>
				<Stack
					direction={{ xs: "column", lg: "row" }}
					alignItems={{ xs: "stretch", lg: "center" }}
					justifyContent="space-between"
					spacing={1.5}
					useFlexGap
					flexWrap="wrap"
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems="stretch"
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ flex: "1 1 620px", minWidth: 0, width: "100%" }}
					>
						{FILTER_KEYS.map((filter) => {
							const options = filterOptionMap[filter.key] || [];
							const selectedOption =
								options.find((option) => String(option.value) === String(filters[filter.key])) || null;
							const displayedOptions = getDisplayedFilterOptions(filter.key, options, selectedOption);

							return (
								<Autocomplete
									key={filter.key}
									size="small"
									disablePortal
									disableClearable={false}
									options={displayedOptions}
									value={selectedOption}
									inputValue={filterInputMap[filter.key]}
									filterOptions={(items) => items}
									loading={filterLoading}
									getOptionLabel={(option) => option?.label || ""}
									isOptionEqualToValue={(option, value) =>
										String(option?.value) === String(value?.value)
									}
									renderOption={(props, option) =>
										filter.key === "user_email" ? (
											<li {...props}>
												<Tooltip title={option?.label || ""} placement="right">
													<Box sx={{ minWidth: 0 }}>
														<Typography
															variant="body1"
															noWrap
															sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
														>
															{option?.displayName || option?.label || ""}
														</Typography>
														{option?.email ? (
															<Typography
																variant="body2"
																color="text.secondary"
																noWrap
																sx={{
																	fontSize: "11px",
																	maxWidth: "100%",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{option.email}
															</Typography>
														) : null}
													</Box>
												</Tooltip>
											</li>
										) : filter.key === "site_id" ? (
											<li {...props}>
												<Box sx={{ minWidth: 0 }}>
													<Typography
														variant="body1"
														noWrap
														sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
													>
														{option?.label || ""}
													</Typography>
													{option?.customerName || option?.customerNumber ? (
														<Typography
															variant="body2"
															color="text.secondary"
															noWrap
															sx={{
																fontSize: "11px",
																maxWidth: "100%",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}}
														>
															{[option.customerName, option.customerNumber].filter(Boolean).join(" - ")}
														</Typography>
													) : null}
												</Box>
											</li>
										) : (
											<li {...props}>{option?.label || ""}</li>
										)
									}
									onInputChange={(_, value) => handleFilterInputChange(filter.key, value)}
									onChange={(_, option) => handleFilterChange(filter.key, option?.value ?? ALL_VALUE)}
									slotProps={{
										popper: {
											placement: "bottom-start",
											modifiers: [{ name: "flip", enabled: false }],
										},
										paper: {
											sx: { mt: 0.5 },
										},
										listbox: {
											sx: { maxHeight: 220, overflow: "auto" },
										},
									}}
									sx={{
										flex: { xs: "1 1 100%", sm: "1 1 210px" },
										minWidth: { xs: "100%", sm: 210 },
										maxWidth: { xs: "100%", lg: 260 },
										"& .MuiOutlinedInput-root": {
											height: 40,
											bgcolor: "background.paper",
										},
										"& .MuiInputBase-input": {
											fontSize: 14,
											py: 0.25,
										},
										"& .MuiInputLabel-root": {
											fontSize: 12,
											transform: "translate(14px, 10px) scale(1)",
											"&.MuiInputLabel-shrink": {
												transform: "translate(14px, -9px) scale(0.75)",
											},
										},
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											size="small"
											label={`Filter by ${filter.label}`}
											placeholder={filter.allLabel}
											variant="outlined"
										/>
									)}
								/>
							);
						})}
						{appliedFilters.length > 0 && (
							<Button
								type="button"
								variant="outlined"
								size="small"
								endIcon={<FilterAltOffIcon />}
								onClick={handleReset}
								sx={{
									height: 40,
									minWidth: { xs: "100%", sm: 108 },
									alignSelf: { xs: "stretch", sm: "center" },
									textTransform: "none",
									fontWeight: 700,
								}}
							>
								Clear
							</Button>
						)}
					</Stack>

					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems="center"
						justifyContent={{ xs: "flex-start", lg: "flex-end" }}
						spacing={0.9}
						useFlexGap
						flexWrap="wrap"
						sx={{ flex: "0 0 auto", width: { xs: "100%", lg: "auto" }, ml: { lg: "auto" } }}
					>
						<Stack direction="row" alignItems="center" spacing={0.75}>
							{DATE_RANGE_BUTTONS.map((button) => {
								const selected = selectedRange.key === button.key;
								return (
									<Tooltip key={button.key} title={button.label} arrow placement="top">
										<Button
											type="button"
											aria-label={button.label}
											variant="roundedGreyBgSquareContained"
											onClick={() => handleDateRangeClick(button.key)}
											sx={{
												background: selected
													? (theme) => theme.palette.primary.main
													: (theme) => alpha(theme.palette.primary.light, 0.2),
												color: selected ? "#fff" : (theme) => theme.palette.primary.main,
											}}
										>
											{button.key}
										</Button>
									</Tooltip>
								);
							})}
						</Stack>

						<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

						<Box sx={{ width: { xs: "100%", sm: 300 } }}>
							<DateRange
								setSelectedRange={() => { }}
								selectedRange={selectedRange}
								selectedDateRange={selectedDateRange}
								setSelectedDateRange={handleDateRangePickerChange}
							/>
						</Box>
					</Stack>
				</Stack>

				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "flex-start", md: "center" }}
					justifyContent="space-between"
					spacing={2}
					sx={{ mt: 2.5 }}
				>
					<Box sx={{ minWidth: 180 }}>
						<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
							{selectedRange.label}
						</Typography>
						<Typography variant="body1" sx={{ color: "#111827" }}>
							{formatDisplayDate(selectedRange.startDate)} - {formatDisplayDate(selectedRange.endDate)}
						</Typography>
					</Box>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent={{ xs: "flex-start", md: "flex-end" }}
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ minHeight: 32, flex: "1 1 auto" }}
					>
						{appliedFilters.length > 0 ? (
							<>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<FilterAltIcon color="primary" fontSize="small" />
									<Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
										Applied Filters :
									</Typography>
								</Stack>
								{appliedFilters.map((filter) => (
									<Chip
										key={filter.key}
										size="small"
										label={filter.display}
										onDelete={() => handleClearFilter(filter.key)}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))}
							</>
						) : null}
					</Stack>
				</Stack>
				<Box sx={{ mt: 1.5, height: 4 }}>{loading ? <LinearProgress /> : null}</Box>
			</Paper>

			{error ? (
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: "error.main" }}>
					<Typography color="error">{error}</Typography>
				</Paper>
			) : null}

			{filterError ? (
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: "warning.main" }}>
					<Typography color="warning.main">{filterError}</Typography>
				</Paper>
			) : null}

			{loading && !hasLoadedDashboard ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<ChartLoading />
				</Box>
			) : (
				<Box>
					<Paper
						elevation={0}
						sx={{
							p: { xs: 2, md: 2.5 },
							mb: 3,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							boxShadow: "none",
							backgroundColor: "background.paper",
						}}
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems={{ xs: "flex-start", sm: "center" }}
							justifyContent="space-between"
							spacing={1.5}
							sx={{ mb: 2 }}
						>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
									Engagement Overview
								</Typography>
							</Box>
						</Stack>

						<Grid container spacing={2}>
							{featuredSummaryCards.map((card) => (
								<Grid key={card.label} item xs={12} sm={6} md={4}>
									<MetricCard
										{...card}
										loading={isRefreshingDashboard}
										onClick={detailCardMap[card.label] ? () => handleMetricCardClick(card.label) : undefined}
									/>
								</Grid>
							))}
						</Grid>

						<Divider sx={{ my: 2.25 }} />

						<Grid container spacing={1.5}>
							{activitySummaryCards.map((card) => (
								<Grid key={card.label} item xs={12} sm={6} md={2.4}>
									<MetricCard
										{...card}
										compact
										loading={isRefreshingDashboard}
										onClick={detailCardMap[card.label] ? () => handleMetricCardClick(card.label) : undefined}
									/>
								</Grid>
							))}
						</Grid>
					</Paper>

					<ModalWrapper
						open={detailModal.open}
						setOpen={() => {}}
						onClose={handleCloseDetailModal}
						title={detailModal.title}
						subtitle={detailModal.description}
						width="72vw"
						content={
							detailModal.rows.length ? (
								<Datagrid
									data={detailModal.rows}
									columns={detailModal.columns}
									rowIdField={detailModal.rowIdField || "id"}
									checkboxSelection={false}
									showSearchBar
									autoHeight={false}
									gridHeight={420}
									hideFilterFromColumnMenu
									lockActiveColumnInManageColumns
									preventHidingLastColumn
								/>
							) : (
								<Box sx={{ p: 2 }}>
									<Typography color="text.secondary">
										No data found for the selected filters and date range.
									</Typography>
								</Box>
							)
						}
						actions={
							<Button onClick={handleCloseDetailModal}>Close</Button>
						}
					/>

					{/* <Paper
						elevation={0}
						sx={{
							p: { xs: 2, md: 2.5 },
							mb: 3,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							boxShadow: "none",
							backgroundColor: "background.paper",
						}}
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems={{ xs: "flex-start", sm: "center" }}
							justifyContent="space-between"
							spacing={1.5}
							sx={{ mb: 2 }}
						>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
									Documents
								</Typography>
							</Box>
						</Stack>

						<Grid container spacing={2}>
							{documentSummaryCards.map((card) => (
								<Grid key={card.label} item xs={12} sm={6} md={4}>
									<MetricCard {...card} loading={isRefreshingDashboard} />
								</Grid>
							))}
						</Grid>
					</Paper> */}

					<Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
						<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
							Quote Conversion Funnel
						</Typography>
						{isRefreshingDashboard ? (
							renderContentLoader(420)
						) : (
							<>
								{dashboard.conversionSankey?.links?.length ? (
									<Box sx={{ height: { xs: 360, md: 420 }, mb: 2 }}>
										<ReactECharts
											option={funnelSankeyOption}
											style={{ height: "100%", width: "100%" }}
											notMerge
											lazyUpdate
										/>
									</Box>
								) : null}
								{/* <TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Step</TableCell>
												<TableCell align="right">Sessions</TableCell>
												<TableCell align="right">Users</TableCell>
												<TableCell align="right">From Start</TableCell>
												<TableCell align="right">From Previous</TableCell>
												<TableCell align="right">Drop-off</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{(dashboard.conversionFunnel || []).map((step) => (
												<TableRow key={step.key}>
													<TableCell sx={{ minWidth: 220 }}>{step.label}</TableCell>
													<TableCell align="right">{formatNumber(step.sessions)}</TableCell>
													<TableCell align="right">{formatNumber(step.users)}</TableCell>
													<TableCell align="right">
														<Box sx={{ minWidth: 120 }}>
															<Typography variant="body2" sx={{ mb: 0.5 }}>
																{formatNumber(step.conversionFromStart)}%
															</Typography>
															<LinearProgress
																variant="determinate"
																value={Math.min(100, Number(step.conversionFromStart || 0))}
																sx={{ height: 8, borderRadius: 999 }}
															/>
														</Box>
													</TableCell>
													<TableCell align="right">{formatNumber(step.conversionFromPrevious)}%</TableCell>
													<TableCell align="right">{formatNumber(step.dropoffFromPrevious)}%</TableCell>
												</TableRow>
											))}
											{!dashboard.conversionFunnel?.length ? (
												<TableRow>
													<TableCell colSpan={6}>
														<Typography color="text.secondary">No funnel data for the selected filters.</Typography>
													</TableCell>
												</TableRow>
											) : null}
										</TableBody>
									</Table>
								</TableContainer> */}
							</>
						)}
					</Paper>

					<Grid container spacing={3}>
						<Grid item xs={12} lg={7}>
							<Paper variant="outlined" sx={{ p: 2.5 }}>
								<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
									Top Pages by Traffic
								</Typography>
								{isRefreshingDashboard ? (
									renderContentLoader(260)
								) : (
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Page</TableCell>
													<TableCell align="right">Views</TableCell>
													<TableCell align="right">% of Total</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{aggregatedTopPages.map((row) => (
													<TableRow key={row.page}>
														<TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>
															{row.page}
														</TableCell>
														<TableCell align="right">{formatNumber(row.views)}</TableCell>
														<TableCell align="right">
															<Box sx={{ minWidth: 80 }}>
																<Typography variant="body2" sx={{ mb: 0.5 }}>
																	{formatNumber(row.percentage)}%
																</Typography>
																<LinearProgress
																	variant="determinate"
																	value={Math.min(100, Number(row.percentage || 0))}
																	sx={{ height: 8, borderRadius: 999 }}
																/>
															</Box>
														</TableCell>
													</TableRow>
												))}
												{!dashboard.topPages?.length ? (
													<TableRow>
														<TableCell colSpan={3}>
															<Typography color="text.secondary">No page data for the selected filters.</Typography>
														</TableCell>
													</TableRow>
												) : null}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Paper>
						</Grid>

						<Grid item xs={12} lg={5}>
							<Paper variant="outlined" sx={{ p: 2.5 }}>
								<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
									Top Users
								</Typography>
								{isRefreshingDashboard ? (
									renderContentLoader(260)
								) : (
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>User</TableCell>
													<TableCell align="right">Page Views</TableCell>
													<TableCell align="right">Logins</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(dashboard.topUsers || []).map((row) => (
													<TableRow key={`${row.user_id}-${row.user_email}`}>
														<TableCell>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																{row.user_name || row.user_email || row.user_id}
															</Typography>
															<Typography variant="caption" color="text.secondary">
																{row.user_email || row.user_id}
															</Typography>
														</TableCell>
														<TableCell align="right">{formatNumber(row.pageViews)}</TableCell>
														<TableCell align="right">{formatNumber(row.logins)}</TableCell>
													</TableRow>
												))}
												{!dashboard.topUsers?.length ? (
													<TableRow>
														<TableCell colSpan={3}>
															<Typography color="text.secondary">No user data for the selected filters.</Typography>
														</TableCell>
													</TableRow>
												) : null}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Paper>
						</Grid>
					</Grid>

				</Box>
			)}
		</Box>
	);
};

export default UserAnalytics;
